import { useQuery } from '@apollo/client';

import { PricingPlanFee } from '__generated__/GQL';
import { formatAmount } from 'common/formatAmount';
import formatPercent from 'common/formatPercent';
import useCompanyContext from 'common/hooks/useCompanyContext';

import { InternationalTransferPayoutFeesDocument } from './graphql/internationalTransferPayoutFees.gql';
import locales from './locales';

type InternationalTransferPayoutFees = PricingPlanFee & {
  minimum: number;
  variable: number;
};
export const useInternationalTransferPayoutFees = (currency = 'EUR') => {
  const { companyProfileId } = useCompanyContext();

  if (!companyProfileId) {
    throw new Error(
      'companyProfileId is required in useInternationalTransferPayoutFees',
    );
  }

  const { data, error, loading, refetch } = useQuery(
    InternationalTransferPayoutFeesDocument,
    {
      variables: {
        companyProfileId: companyProfileId,
      },
    },
  );

  if (loading) {
    return {
      isLoading: loading,
    };
  }

  if (error) {
    return {
      error,
      isLoading: loading,
    };
  }

  const internationalTransferPayoutFees = data?.viewer?.company?.currentPlan
    ?.fees.internationalTransferPayout as InternationalTransferPayoutFees;

  const tooltipText = locales.formatString(locales.toolTip, {
    minimumFee: formatAmount({
      currency,
      currencyDisplay: 'symbol',
      forcePlusSign: false,
      isMinorUnitValue: true,
      value: internationalTransferPayoutFees.minimum,
    }),
    variableFee: formatPercent(internationalTransferPayoutFees.variable),
  });

  return {
    error,
    internationalTransferPayoutFees,
    isLoading: loading,
    refetch,
    tooltipText,
  };
};
